import React from "react";
import { useApi } from "../hooks/useApi";
import { useWebSocket } from "../hooks/useWebSocket";
import { format } from "date-fns";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "14px" };

function actionColor(a) {
  if (a === "BUY")          return "#4ade80";
  if (a === "SELL")         return "#f87171";
  if (a === "CLOSE" || a === "CLOSE_ALL") return "#f59e0b";
  if (a === "ADJUST_SL")    return "#60a5fa";
  if (a === "PARTIAL_CLOSE") return "#a78bfa";
  if (a === "REVERSE")      return "#fb923c";
  return "#64748b";
}

function actionIcon(a) {
  if (a === "BUY")           return "🟢";
  if (a === "SELL")          return "🔴";
  if (a === "CLOSE" || a === "CLOSE_ALL") return "🟠";
  if (a === "ADJUST_SL")     return "🔒";
  if (a === "PARTIAL_CLOSE") return "✂️";
  if (a === "REVERSE")       return "🔄";
  if (a === "HOLD")          return "⏸";
  return "•";
}

export default function SignalFeed() {
  const { data }   = useApi("/intelligence", 15000);
  const { messages } = useWebSocket("events");

  // Combine pending signals from REST + live WS events
  const pending = data?.pending_signals || [];

  // Recent WS trade events
  const wsEvents = messages
    .filter(m => ["trade_opened","trade_closed","sl_modified","brain_event"].includes(m.type))
    .slice(0, 12);

  return (
    <div style={card}>
      <div style={{ fontWeight: 600, color: "#94a3b8", marginBottom: "10px", fontSize: "13px" }}>
        📡 Signals to MT5
      </div>

      {/* Pending signals awaiting execution */}
      {pending.length > 0 && (
        <div style={{ marginBottom: "10px" }}>
          <div style={{ fontSize: "10px", color: "#475569", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Awaiting Execution
          </div>
          {pending.map((s) => (
            <div key={s.symbol} style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "5px 8px", borderRadius: "6px", marginBottom: "3px",
              background: s.status === "OVERDUE" ? "#1a0a0a" : "#0f172a",
              border: `1px solid ${s.status === "OVERDUE" ? "#7f1d1d" : "#1e2a45"}`,
            }}>
              <span style={{ fontSize: "12px" }}>{actionIcon(s.action)}</span>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", width: "65px" }}>{s.symbol}</span>
              <span style={{ fontSize: "11px", color: actionColor(s.action), fontWeight: 600 }}>{s.action}</span>
              <span style={{ fontSize: "10px", color: "#475569", marginLeft: "auto" }}>{s.lots}L</span>
              <span style={{ fontSize: "10px", color: s.status === "OVERDUE" ? "#f87171" : "#64748b" }}>
                {s.age_sec}s {s.retries > 0 ? `(retry ${s.retries})` : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Live signal stream */}
      <div style={{ fontSize: "10px", color: "#475569", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Recent Signals
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", maxHeight: "220px", overflowY: "auto" }}>
        {wsEvents.length === 0 && (
          <div style={{ color: "#334155", fontSize: "11px", padding: "10px 0" }}>Waiting for signals...</div>
        )}
        {wsEvents.map((m, i) => {
          const isEntry = m.type === "trade_opened";
          const isClose = m.type === "trade_closed";
          const isSL    = m.type === "sl_modified";
          const isBrain = m.type === "brain_event";

          let label = "";
          if (isEntry) label = `${m.symbol} ${m.action} ${m.lots}L @ ${m.price} [${m.tranche || ""}]`;
          else if (isClose) label = `${m.symbol} CLOSED $${m.profit?.toFixed(2)} — ${m.reason}`;
          else if (isSL)    label = `${m.symbol} SL → ${m.new_sl?.toFixed(5)} (${m.reason})`;
          else if (isBrain) label = `${m.symbol} ${m.action} ${m.confidence ? (m.confidence*100).toFixed(0)+"%" : ""} ${m.gate ? "⛔"+m.gate.split(":")[0] : ""}`;

          return (
            <div key={i} style={{
              display: "flex", gap: "6px", alignItems: "center",
              padding: "3px 6px", borderRadius: "4px",
              borderLeft: `2px solid ${actionColor(m.action || (isClose ? "CLOSE" : isSL ? "ADJUST_SL" : "HOLD"))}`,
              background: i === 0 ? "#0f172a" : "transparent",
            }}>
              <span style={{ fontSize: "11px" }}>{actionIcon(m.action || (isClose ? "CLOSE" : isSL ? "ADJUST_SL" : "HOLD"))}</span>
              <span style={{ fontSize: "10px", color: "#94a3b8", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
              </span>
              <span style={{ fontSize: "9px", color: "#334155", flexShrink: 0 }}>
                {m.ts ? format(new Date(m.ts), "HH:mm:ss") : ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
